import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { toast } from 'react-hot-toast';
import { Loader2, ThumbsUp, ThumbsDown, RefreshCw } from 'lucide-react';
import { chatApi, fmtDate, fmtMoney, fmtTry } from './api';
import { Panel, Badge, Segmented, inputCls, btnGhost } from './ui';

const STATUS_TONE = { bot: 'green', needs_human: 'amber', human: 'indigo', closed: 'gray' };

function ProductMini({ p }) {
  return (
    <a href={p.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-1.5 hover:bg-gray-50">
      {p.image ? <img src={p.image} alt="" className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 rounded bg-gray-100" />}
      <div className="min-w-0">
        <div className="text-xs text-gray-800 line-clamp-2">{p.title}</div>
        <div className="text-xs text-gray-500">{fmtTry(p.price)}{p.inStock === false ? ' · stokta yok' : ''}</div>
      </div>
    </a>
  );
}

function Block({ b }) {
  if (b.type === 'text') return <div className="whitespace-pre-wrap">{b.text}</div>;
  if (b.type === 'products') return <div className="grid gap-1.5 mt-1">{(b.items || []).map((p) => <ProductMini key={p.id} p={p} />)}</div>;
  if (b.type === 'quick_replies') return <div className="flex flex-wrap gap-1 mt-1">{(b.options || []).map((o, i) => <Badge key={i} tone="indigo">{typeof o === 'string' ? o : o.label || o.text}</Badge>)}</div>;
  if (b.type === 'action') {
    const label = b.kind === 'add_to_cart' ? `Sepete ekle: ${b.title || b.productId}` : b.kind === 'open_url' ? `Bağlantı: ${b.label || b.url}` : b.kind === 'handoff_form' ? `İnsana devir formu (${b.reason || '-'})` : b.kind;
    return <div className="mt-1"><Badge tone="amber">{label}</Badge></div>;
  }
  return <pre className="text-xs text-gray-400 whitespace-pre-wrap">{JSON.stringify(b)}</pre>;
}

const STATUS_LABEL = { bot: 'Bot', needs_human: 'İnsan bekliyor', human: 'İnsanda', closed: 'Kapalı' };
const TOOL_LABEL = { search_products: 'Ürün arama', get_product: 'Ürün detayı', show_products: 'Kart gösterme', get_info: 'Bilgi arama', suggest_add_to_cart: 'Sepete ekle önerisi', open_page: 'Sayfa yönlendirme', request_handoff: 'İnsana devir' };

function ToolCalls({ calls }) {
  if (!Array.isArray(calls) || !calls.length) return null;
  return (
    <details className="mt-1.5 text-[11px] text-gray-500">
      <summary className="cursor-pointer select-none">{calls.length} araç çağrısı: {calls.map((c) => TOOL_LABEL[c.name] || c.name).join(', ')}</summary>
      <pre className="mt-1 whitespace-pre-wrap break-all rounded bg-white/70 border border-gray-200 p-2 text-[11px] text-gray-600">{JSON.stringify(calls.map((c) => ({ [c.name]: c.input })), null, 1)}</pre>
    </details>
  );
}

function Stat({ label, children }) {
  return <div><div className="text-[11px] text-gray-400">{label}</div><div className="text-sm font-medium text-gray-800 truncate">{children}</div></div>;
}

function ConversationDetail({ id }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    chatApi(`/conversations/${id}`).then((d) => alive && setData(d)).catch((e) => toast.error(e.message));
    return () => { alive = false; };
  }, [id]);
  if (!data) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  const c = data.conversation;
  const flags = [c.product_clicked && 'Ürüne tıkladı', c.added_to_cart && 'Sepete ekledi', c.checkout_started && 'Ödemeye geçti', c.lead_id && 'Lead bıraktı'].filter(Boolean);
  return (
    <div className="flex-1 min-w-0 flex flex-col">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
        <Stat label="Başlangıç">{fmtDate(c.created_at)}</Stat>
        <Stat label="Giriş sayfası">{c.entry_path || '-'}</Stat>
        <Stat label="Kaynak">{c.utm_source || 'doğrudan'}</Stat>
        <Stat label="Token">{(c.tokens_in || 0).toLocaleString('tr-TR')} / {(c.tokens_out || 0).toLocaleString('tr-TR')}</Stat>
        <Stat label="Maliyet">{fmtMoney(c.cost_usd)}</Stat>
        <div className="col-span-full flex flex-wrap gap-1.5">
          <Badge tone={STATUS_TONE[c.status] || 'gray'}>{STATUS_LABEL[c.status] || c.status}</Badge>
          {c.is_preview && <Badge tone="amber">Önizleme</Badge>}
          {flags.map((f) => <Badge key={f} tone="indigo">{f}</Badge>)}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {(data.messages || []).map((m) => {
          const user = m.role === 'user';
          const blocks = Array.isArray(m.blocks) ? m.blocks : [{ type: 'text', text: m.text || m.content || '' }];
          return (
            <div key={m.id} className={`flex ${user ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm ${user ? 'bg-indigo-600 text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'}`}>
                {blocks.map((b, i) => <Block key={i} b={b} />)}
                {!user && <ToolCalls calls={m.tool_calls} />}
                <div className={`text-[10px] mt-1 flex items-center gap-1.5 ${user ? 'text-indigo-200' : 'text-gray-400'}`}>
                  {fmtDate(m.created_at)}
                  {!user && m.latency_ms ? <span>· {(m.latency_ms / 1000).toFixed(1)} sn</span> : null}
                  {m.feedback === 1 && <ThumbsUp className="w-3 h-3" />}
                  {m.feedback === -1 && <ThumbsDown className="w-3 h-3" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ConversationsTab() {
  const [filters, setFilters] = useState({ status: '', from: '', to: '', preview: false });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await chatApi('/conversations', { query: { status: filters.status, from: filters.from, to: filters.to, preview: filters.preview ? 1 : 0, limit: 100 } });
      const list = r.items || [];
      setItems(list);
      setOpenId((cur) => (cur && list.some((c) => c.id === cur) ? cur : list[0]?.id || null));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden flex flex-col lg:flex-row min-h-[520px]">
      <div className="lg:w-[340px] shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200 flex flex-col min-h-0">
        <div className="p-3 border-b border-gray-100 space-y-2">
          <div className="flex items-center gap-2">
            <select className={`${inputCls} !py-1 !text-xs`} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} aria-label="Durum">
              <option value="">Tüm durumlar</option><option value="bot">Bot</option><option value="needs_human">İnsan bekliyor</option><option value="human">İnsanda</option><option value="closed">Kapalı</option>
            </select>
            <button className={btnGhost} onClick={load} title="Yenile"><RefreshCw className={`${loading ? 'animate-spin' : ''}`} /></button>
          </div>
          <div className="flex items-center gap-2">
            <input type="date" className={`${inputCls} !py-1 text-xs`} value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} aria-label="Başlangıç" />
            <input type="date" className={`${inputCls} !py-1 text-xs`} value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} aria-label="Bitiş" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={filters.preview} onChange={(e) => setFilters({ ...filters, preview: e.target.checked })} />Panel önizlemelerini göster</label>
        </div>
        <div className="flex-1 overflow-y-auto">
          {!loading && !items.length && <div className="p-6 text-center text-sm text-gray-400">Bu filtrede konuşma yok</div>}
          {items.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setOpenId(c.id)}
              className={`w-full text-left px-3.5 py-3 border-b border-gray-100 border-l-2 ${openId === c.id ? 'border-l-indigo-600 bg-indigo-50/60' : 'border-l-transparent hover:bg-gray-50'}`}
            >
              <div className="flex items-center gap-2 text-[11px] text-gray-500">
                <span>{fmtDate(c.last_msg_at || c.created_at)}</span>
                <span className="ml-auto">{c.msg_count} mesaj · {fmtMoney(c.cost_usd)}</span>
              </div>
              <div className="text-xs font-medium text-gray-800 mt-0.5 line-clamp-2">{c.first_msg || <span className="text-gray-400">(mesaj yok)</span>}</div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                <Badge tone={STATUS_TONE[c.status] || 'gray'}>{STATUS_LABEL[c.status] || c.status}</Badge>
                {c.is_preview && <Badge tone="amber">önizleme</Badge>}
                {c.added_to_cart ? <Badge tone="indigo">sepete ekledi</Badge> : c.product_clicked ? <Badge tone="indigo">ürüne tıkladı</Badge> : null}
                {c.lead_id && <Badge tone="indigo">lead</Badge>}
              </div>
            </button>
          ))}
        </div>
      </div>
      {openId ? <ConversationDetail id={openId} /> : <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Soldan bir konuşma seç</div>}
    </div>
  );
}

const LEAD_STATUS = { new: 'Yeni', contacted: 'Arandı', won: 'Kazanıldı', lost: 'Kaybedildi' };

export function LeadsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await chatApi('/leads')).items || []); } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (l, status) => {
    try { await chatApi(`/leads/${l.id}`, { method: 'PUT', body: { status } }); setItems((xs) => xs.map((x) => (x.id === l.id ? { ...x, status } : x))); }
    catch (e) { toast.error(e.message); }
  };
  const g = (l, a, b) => l[a] ?? l[b];
  return (
    <Panel title="Lead'ler" actions={<button className={btnGhost} onClick={load}>Yenile</button>}>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[11px] uppercase tracking-wide text-gray-500 text-left"><tr><th className="py-1.5">Tarih</th><th>Kişi</th><th>Not / neden</th><th>Onay</th><th>Durum</th></tr></thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-t border-gray-100 align-top">
                  <td className="py-1.5 whitespace-nowrap">{fmtDate(g(l, 'createdAt', 'created_at'))}</td>
                  <td><div className="font-medium">{l.name}</div><div className="text-xs text-gray-500">{l.phone} {l.email}</div></td>
                  <td className="max-w-[220px]"><div className="text-xs">{l.note}</div>{l.reason && <div className="text-xs text-gray-400">{l.reason}</div>}</td>
                  <td className="space-x-1 whitespace-nowrap">
                    {g(l, 'consentKvkk', 'consent_kvkk') ? <Badge tone="green">KVKK</Badge> : <Badge tone="red">KVKK yok</Badge>}
                    {g(l, 'consentIys', 'consent_iys') ? <Badge tone="green">İYS</Badge> : <Badge tone="gray">İYS yok</Badge>}
                  </td>
                  <td>
                    <select className="rounded-md border border-gray-200 bg-white text-xs px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-200" value={l.status || 'new'} onChange={(e) => setStatus(l, e.target.value)}>
                      {Object.entries(LEAD_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={5} className="py-4 text-center text-gray-400">Lead yok</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function iso(d) { return d.toISOString().slice(0, 10); }
const RANGES = [{ days: 7, label: '7 gün' }, { days: 30, label: '30 gün' }, { days: 90, label: '90 gün' }];
const BAR = '#4f46e5';

function Kpi({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3">
      <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-xl font-semibold text-gray-900 mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function DailyTip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow">
      <div className="font-medium text-gray-800 mb-0.5">{d.label}</div>
      <div className="text-gray-600">{d.conversations} konuşma · {d.messages} mesaj</div>
      <div className="text-gray-400">{fmtMoney(d.cost_usd)}</div>
    </div>
  );
}

export function AnalyticsTab() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const range = useMemo(() => ({ from: iso(new Date(Date.now() - (days - 1) * 864e5)), to: iso(new Date()) }), [days]);
  useEffect(() => {
    setLoading(true);
    chatApi('/analytics', { query: range }).then(setData).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [range]);
  const d = data || {};
  // Boş günler de çubuk ekseninde görünsün diye seriyi aralığın tüm günleriyle doldur
  const series = useMemo(() => {
    const by = Object.fromEntries((d.daily || []).map((x) => [x.day, x]));
    const out = [];
    for (let t = new Date(range.from); iso(t) <= range.to; t = new Date(t.getTime() + 864e5)) {
      const k = iso(t);
      const x = by[k] || {};
      out.push({ day: k, label: t.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }), conversations: x.conversations || 0, messages: x.messages || 0, cost_usd: x.cost_usd || 0 });
    }
    return out;
  }, [d.daily, range]);
  const conv = d.conversations || 0;
  const funnel = [
    ['Konuşma başlattı', conv],
    ['Ürün kartına tıkladı', d.productClicked || 0],
    ['Sepete ekledi', d.addedToCart || 0],
    ['Lead / devir', Math.max(d.leads || 0, d.handoffs || 0)],
  ];
  const pct = (n) => (conv ? `%${Math.round((n / conv) * 100)}` : '–');
  const v = (x) => (loading ? '…' : x);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented value={days} onChange={setDays} options={RANGES.map((r) => ({ value: r.days, label: r.label }))} />
        <span className="text-[11px] text-gray-400">Panel önizlemeleri dahil değil. Sipariş atfı v2'de.</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi label="Konuşma" value={v(conv)} sub={v(`${d.messages || 0} mesaj`)} />
        <Kpi label="Ürüne tıklama" value={v(d.productClicked || 0)} sub={v(pct(d.productClicked || 0))} />
        <Kpi label="Sepete ekleme" value={v(d.addedToCart || 0)} sub={v(pct(d.addedToCart || 0))} />
        <Kpi label="Lead" value={v(d.leads || 0)} sub={v(`${d.handoffs || 0} devir`)} />
        <Kpi label="Ort. yanıt süresi" value={v(d.avgLatencyMs ? `${(d.avgLatencyMs / 1000).toFixed(1)} sn` : '–')} sub={v(`+${d.feedbackUp || 0} / −${d.feedbackDown || 0} beğeni`)} />
        <Kpi label="Maliyet" value={v(fmtMoney(d.costUsd))} sub={v(conv ? `${fmtMoney((d.costUsd || 0) / conv)} / konuşma` : '')} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">
        <Panel title="Günlük konuşma" hint="Çubuğun üstüne gel: mesaj ve maliyet">
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} interval="preserveStartEnd" minTickGap={16} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
                <Tooltip content={<DailyTip />} cursor={{ fill: 'rgba(79,70,229,0.06)' }} />
                <Bar dataKey="conversations" fill={BAR} radius={[4, 4, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Huni" hint="Konuşma başlatanların yüzdesi">
          <div className="space-y-3">
            {funnel.map(([label, n]) => (
              <div key={label}>
                <div className="flex justify-between text-xs mb-1"><span className="text-gray-700">{label}</span><span className="text-gray-500 tabular-nums">{v(n)} · {v(pct(n))}</span></div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: conv ? `${Math.max((n / conv) * 100, n ? 2 : 0)}%` : 0, background: BAR }} /></div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      <Panel title="Ziyaretçilerin ilk sorusu" hint="Konuşmayı açan mesajlar, en sıktan">
        {(d.topQuestions || []).length ? (
          <ol className="divide-y divide-gray-100">
            {d.topQuestions.map((q, i) => (
              <li key={q.q} className="flex items-center gap-3 py-2 text-xs">
                <span className="w-5 text-xs text-gray-400 tabular-nums">{i + 1}</span>
                <span className="flex-1 text-gray-800">{q.q}</span>
                <span className="text-xs text-gray-500 tabular-nums">{q.n}×</span>
              </li>
            ))}
          </ol>
        ) : <p className="text-sm text-gray-400">{loading ? 'Yükleniyor…' : 'Bu aralıkta konuşma yok'}</p>}
      </Panel>
    </div>
  );
}
